import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { blogImages } from "@/lib/blogImages";

export default function BlogPost() {
    const { slug } = useParams();
    const { t } = useLanguage();
    const post = t.blog.posts.find((p) => p.slug === slug);

    if (!post) return <Navigate to="/blog" replace />;

    return (
        <div data-testid="blog-post-page" className="pt-36 sm:pt-44">
            <article className="mx-auto max-w-3xl px-5 sm:px-8">
                <Reveal>
                    <Link
                        to="/blog"
                        data-testid="blog-back-link"
                        className="group inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-neon"
                    >
                        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                        {t.blogPage.back}
                    </Link>

                    <div className="mt-8 flex flex-wrap items-center gap-3 font-mono text-[11px] text-slate-500">
                        <span className="rounded-full border border-neon/30 bg-neon/10 px-3 py-1 uppercase tracking-widest text-neon">
                            {post.category}
                        </span>
                        <span>{post.date}</span>
                        <span>·</span>
                        <span>{post.readTime}</span>
                    </div>

                    <h1 className="mt-6 font-display text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                        {post.title}
                    </h1>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-10 overflow-hidden rounded-2xl border border-slate-800">
                        <img
                            src={blogImages[post.slug]}
                            alt={post.title}
                            className="aspect-video w-full object-cover"
                        />
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-10 space-y-6" data-testid="blog-post-body">
                        {post.body.map((para, i) => (
                            <p key={i} className="text-sm leading-[1.9] text-slate-300 sm:text-base">
                                {para}
                            </p>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="glass mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl p-8 sm:flex-row sm:items-center">
                        <p className="font-display text-lg font-bold text-white">{t.blogPage.relatedCta}</p>
                        <Link
                            to="/lien-he"
                            data-testid="blog-post-cta-button"
                            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-neon px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white"
                        >
                            {t.blogPage.relatedCtaButton}
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </Reveal>
            </article>
            <div className="pb-28" />
        </div>
    );
}
